import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import LotteryWeekSelector, {
  getInitialWeekStart,
  getWeekRange,
} from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import type { TicketReceivedItem, TicketReceivedTableRow } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

// ────────────────────────────────────────
// 상수
// ────────────────────────────────────────

const STORAGE_KEY = 'avatye.ops.lucky-lottery.ticket.received.filter';

const LABEL_ORDER = [
  '광고보기',
  '영상광고',
  '럭키복권 광고보기',
  '럭키복권 영상광고',
  '버즈빌 S2S',
  '네이버 플레이스',
  '출석체크',
  '웰컴보상',
  '홈방문하기',
  'RC',
] as const;

const LABEL_COLORS: Record<string, string> = {
  광고보기: '#0044D2',
  영상광고: '#22c55e',
  '럭키복권 광고보기': '#6366f1',
  '럭키복권 영상광고': '#84cc16',
  '버즈빌 S2S': '#f97316',
  '네이버 플레이스': '#10b981',
  출석체크: '#f59e0b',
  웰컴보상: '#a855f7',
  홈방문하기: '#ef4444',
  RC: '#06b6d4',
};

// ────────────────────────────────────────
// 필터 영속화 헬퍼
// ────────────────────────────────────────

type StoredFilter = {
  startDate: string;
  endDate: string;
  appName: string;
};

const getStoredFilter = (): StoredFilter | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilter;
  } catch {
    // ignore
  }
  return null;
};

const saveFilter = (filter: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
};

// ────────────────────────────────────────
// 커스텀 툴팁
// ────────────────────────────────────────

type TooltipPayloadItem = {
  name: string;
  value: number;
  color: string;
};

function CustomLineTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        p: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#1e293b' }}>
        {label}
      </Typography>
      {payload.map((item) => (
        <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>
            {item.name}: <strong>{Number(item.value).toLocaleString('ko-KR')}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ────────────────────────────────────────
// 페이지 컴포넌트
// ────────────────────────────────────────

export default function TicketReceived() {
  // ── 필터 초기값 (localStorage 복원)
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate) {
      return { startDate: stored.startDate, endDate: stored.endDate };
    }
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState<string>(initialWeek.startDate);
  const [endDate, setEndDate] = useState<string>(initialWeek.endDate);
  const [appName, setAppName] = useState<string>(stored?.appName ?? '앱 전체');
  const [selectedOs, setSelectedOs] = useState<string>('all');

  // ── 앱 목록
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  // ── 데이터 상태
  const [data, setData] = useState<TicketReceivedItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── 앱 목록 조회
  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => {
        setAppOptions(['앱 전체', ...(res.data || [])]);
      })
      .catch(() => undefined);
  }, [startDate, endDate]);

  // ── 데이터 조회
  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;

    const app = appName === '앱 전체' ? null : appName;

    // 필터 저장
    saveFilter({ startDate, endDate, appName });

    setLoading(true);
    apiClient
      .get<TicketReceivedItem[]>('/api/v1/analytics/lucky-lottery/ticket/received', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, appName, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── 차트 데이터 가공 (날짜 × label별 피벗)
  const chartData = useMemo(() => {
    if (!data?.length) return [];

    return [...data]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((item) => {
        const byLabel: Record<string, number> = {};
        for (const label of LABEL_ORDER) {
          const found = (item.details ?? []).find((d) => d.label === label);
          byLabel[label] = found ? found.ticketSum : 0;
        }
        return { date: item.date, ...byLabel };
      });
  }, [data]);

  // ── 테이블 데이터 가공
  const tableRows = useMemo((): TicketReceivedTableRow[] => {
    if (!data?.length) return [];

    return [...data]
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((item) => {
        const labels: TicketReceivedTableRow['labels'] = {};
        for (const label of LABEL_ORDER) {
          const found = (item.details ?? []).find((d) => d.label === label);
          labels[label] = found
            ? { ticketSum: found.ticketSum, ticketSumPerDau: found.ticketSumPerDau }
            : { ticketSum: 0, ticketSumPerDau: 0 };
        }
        return {
          date: item.date,
          wau: item.wau,
          dau: item.dau,
          totalTicketSum: item.totalTicketSum,
          totalTicketSumPerDau: item.totalTicketSumPerDau,
          labels,
        };
      });
  }, [data]);

  return (
    <Box>
      {/* ── 상단 필터 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>

        <Box
          sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}
        >
          <FilterSelectorWithApp
            selectedApp={appName}
            appOptions={appOptions}
            onAppChange={(v) => setAppName(v ?? '앱 전체')}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* ── 본문 카드 */}
      <Box sx={PAGE_CARD_SX}>
        {/* 앱 필터 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setAppName(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: appName === name ? 700 : 400,
                backgroundColor: appName === name ? '#0044D2' : '#fff',
                color: appName === name ? '#fff' : '#374151',
                border: `1px solid ${appName === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: appName === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

        {/* ── 로딩 / 에러 / 콘텐츠 분기 */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} gap={2}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1">데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* ── 차트 섹션 */}
            <Box sx={{ mb: 4 }}>
              {/* 차트 헤더 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                    일별 응모권 발급 추이
                  </Typography>
                  <Tooltip
                    title="날짜별, 서브타입(label)별 응모권 발급 현황입니다."
                    arrow
                    placement="right"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </Box>

              {/* 라인 차트 */}
              {chartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: 320, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => dayjs(v).format('MM/DD')}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => Number(v).toLocaleString('ko-KR')}
                        width={56}
                      />
                      <RechartsTooltip
                        content={<CustomLineTooltip />}
                        cursor={{ stroke: '#e5e7eb' }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: '0.8rem', paddingTop: 8 }}
                      />
                      {LABEL_ORDER.map((label) => (
                        <Line
                          key={label}
                          type="monotone"
                          dataKey={label}
                          name={label}
                          stroke={LABEL_COLORS[label] ?? '#94a3b8'}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* ── 테이블 섹션 */}
            <Box sx={{ pb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  일별 상세 현황
                </Typography>
                <Tooltip
                  title="날짜 내림차순으로 정렬됩니다. 평균 발급 수는 소수 둘째 자리까지 표시합니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {tableRows.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 0, border: '1px solid #f1f5f9' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          rowSpan={2}
                          sx={{
                            fontWeight: 700,
                            bgcolor: '#f8fafc',
                            borderBottom: '2px solid #e2e8f0',
                            minWidth: 90,
                            fontSize: '0.82rem',
                            color: '#475569',
                            verticalAlign: 'middle',
                          }}
                        >
                          날짜
                        </TableCell>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            fontSize: '0.82rem',
                            color: '#475569',
                            borderLeft: '1px solid #e2e8f0',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          합계
                        </TableCell>
                        {LABEL_ORDER.map((label) => (
                          <TableCell
                            key={label}
                            colSpan={2}
                            align="center"
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#f8fafc',
                              borderBottom: '1px solid #e2e8f0',
                              fontSize: '0.82rem',
                              color: LABEL_COLORS[label] ?? '#475569',
                              borderLeft: '1px solid #e2e8f0',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {(['WAU', 'DAU', '발급 수', '평균'] as const).map((sub) => (
                          <TableCell
                            key={`합계-${sub}`}
                            align="right"
                            sx={{
                              fontWeight: 600,
                              bgcolor: '#f8fafc',
                              borderBottom: '2px solid #e2e8f0',
                              fontSize: '0.75rem',
                              color: '#64748b',
                              py: 0.5,
                              px: 1,
                              borderLeft: sub === 'WAU' ? '1px solid #e2e8f0' : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sub}
                          </TableCell>
                        ))}
                        {LABEL_ORDER.map((label) =>
                          (['발급 수', '평균'] as const).map((sub) => (
                            <TableCell
                              key={`${label}-${sub}`}
                              align="right"
                              sx={{
                                fontWeight: 600,
                                bgcolor: '#f8fafc',
                                borderBottom: '2px solid #e2e8f0',
                                fontSize: '0.75rem',
                                color: '#64748b',
                                py: 0.5,
                                px: 1,
                                borderLeft: sub === '발급 수' ? '1px solid #e2e8f0' : 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sub}
                            </TableCell>
                          )),
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {tableRows.map((row, rowIdx) => (
                        <TableRow
                          key={row.date}
                          hover
                          sx={{
                            '&:hover': { bgcolor: '#f8fafc' },
                            bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.82rem',
                              color: '#1e293b',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.date}
                          </TableCell>
                          {/* 합계 */}
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: '0.82rem',
                              color: '#374151',
                              borderLeft: '1px solid #f1f5f9',
                              px: 1,
                            }}
                          >
                            {row.wau.toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: '0.82rem', color: '#374151', px: 1 }}
                          >
                            {row.dau.toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', px: 1 }}
                          >
                            {row.totalTicketSum.toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', px: 1 }}
                          >
                            {row.totalTicketSumPerDau.toFixed(2)}
                          </TableCell>
                          {/* label별 */}
                          {LABEL_ORDER.map((label) => {
                            const cell = row.labels[label];
                            return (
                              <>
                                <TableCell
                                  key={`${label}-sum`}
                                  align="right"
                                  sx={{
                                    fontSize: '0.82rem',
                                    color: '#374151',
                                    borderLeft: '1px solid #f1f5f9',
                                    px: 1,
                                  }}
                                >
                                  {cell.ticketSum.toLocaleString('ko-KR')}
                                </TableCell>
                                <TableCell
                                  key={`${label}-per`}
                                  align="right"
                                  sx={{
                                    fontSize: '0.82rem',
                                    color: '#374151',
                                    px: 1,
                                  }}
                                >
                                  {cell.ticketSumPerDau.toFixed(2)}
                                </TableCell>
                              </>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
